import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import {
  SuiDialogConfig,
  SuiDialogBase,
  DialogCloseReason,
  SuiDialogSimpleComponent,
  SuiDialogHeaderSlot,
  SuiDialogContentSlot,
  SuiDialogFooterSlot,
} from '@sui/dialog';
import { ComboboxOption, SuiComboboxComponent } from '@sui/combobox';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { Observable, tap } from 'rxjs';

import {
  injectWidgetPresetGroupsQuery,
  injectWidgetPresetsQuery,
  WidgetPresetsScope,
} from 'src/app/api/queries/widget.queries';

import { NomenclatureItem, Widget } from 'src/app/core/models/core.models';
import { WidgetPreset } from 'src/app/core/models/widget.models';
import {
  WidgetPreviewEvent,
  WidgetPresetCardComponent,
} from '../../components/widget-preset-card/widget-preset-card.component';
import { SuiButtonComponent } from '@sui/button';
import { SearchInputComponent } from '../../../shared/components/search-input/search-input.component';
import { FormsModule } from '@angular/forms';
import { LoadingComponent } from '../../../shared/components/loading/loading.component';
import { AsyncPipe } from '@angular/common';
import { SortPipe } from '../../../shared/pipes/sort.pipe';

export interface SwapWidgetDialogInput {
  widget: Widget;
  objectTypeId: number;
  objectClassId: number;
}

export interface SwapWidgetDialogOutput {
  chartId: number;
  chartTypeId: number;
}

@Component({
  selector: 'app-swap-widget-dialog',
  templateUrl: './swap-widget-dialog.component.html',
  styleUrls: ['./swap-widget-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SuiDialogSimpleComponent,
    SuiDialogHeaderSlot,
    SuiButtonComponent,
    SuiDialogContentSlot,
    SearchInputComponent,
    SuiComboboxComponent,
    FormsModule,
    LoadingComponent,
    WidgetPresetCardComponent,
    SuiDialogFooterSlot,
    AsyncPipe,
    TranslatePipe,
    SortPipe,
  ],
})
export class SwapWidgetDialogComponent
  extends SuiDialogBase<SwapWidgetDialogInput, SwapWidgetDialogOutput>
  implements OnInit
{
  static dialogConfig: Partial<SuiDialogConfig> = {
    width: '50vw',
    minWidth: 800,
    maxWidth: 1200,
  };
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);

  // Set in ngOnInit, once dialogData is available.
  private readonly presetsScope = signal<WidgetPresetsScope | null>(null);
  private readonly widgetPresetsQuery = injectWidgetPresetsQuery(
    this.presetsScope,
  );
  private readonly widgetPresetGroupsQuery = injectWidgetPresetGroupsQuery();

  widgetPresets$: Observable<WidgetPreset[]> = toObservable(
    computed(() => this.widgetPresetsQuery.data() ?? []),
  );
  widgetPresetsLoading$: Observable<boolean> = toObservable(
    computed(() => this.widgetPresetsQuery.isLoading()),
  );
  private readonly widgetPresetGroups$ = toObservable(
    computed(() => this.widgetPresetGroupsQuery.data() ?? []),
  );
  // Signals: written from the preset and group queries, which run after the
  // template in the same pass (WAP-1954).
  readonly widgetPresetsFiltered = signal<WidgetPreset[]>([]);
  widgetPresets: WidgetPreset[] = [];
  groups: NomenclatureItem[] = [];
  // sui-combobox options derived from `groups` ({ title, id } → { label, value }),
  // rebuilt whenever the groups list changes.
  readonly groupOptions = signal<ComboboxOption[]>([]);

  searchString: string = '';
  selectedPresetGroupId: number = 0;
  selectedPresetId: number = null;
  selectedChartTypeId: number = null;

  selectedEvent: WidgetPreviewEvent = null;

  get widget(): Widget {
    return this.dialogData.widget;
  }

  ngOnInit(): void {
    this.presetsScope.set({
      objectTypeId: this.dialogData.objectTypeId,
      objectClassId: this.dialogData.objectClassId,
    });

    this.widgetPresetGroups$
      .pipe(
        tap((groups) => {
          this.groups = [
            {
              id: 0,
              title: this.translateService.instant(
                'swap-widget-dialog.label.all-widgets',
              ),
            },
            ...groups,
          ];
          this.groupOptions.set(
            this.groups.map((group) => ({
              label: group.title,
              value: group.id,
            })),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.widgetPresets$
      .pipe(
        tap((presets) => {
          this.widgetPresets = presets;
          this.widgetPresetsFiltered.set(presets);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  handleInputChange(searchString: string): void {
    this.searchString = searchString;
    this.filterWidgets(searchString);
  }

  filterWidgets(searchString: string): void {
    const search = searchString.toLowerCase();

    this.widgetPresetsFiltered.set(
      this.widgetPresets.filter((preset) => {
        const matchesSearch =
          search.length === 0 ||
          preset.name?.toLowerCase().includes(search) ||
          preset.description?.toLowerCase().includes(search);

        const matchesGroup =
          this.selectedPresetGroupId === 0 ||
          preset.group_id === this.selectedPresetGroupId;

        return matchesSearch && matchesGroup;
      }),
    );
  }

  onGroupChange(groupId: number): void {
    this.selectedPresetGroupId = groupId;
    this.filterWidgets(this.searchString);
  }

  onWidgetPreview(event: WidgetPreviewEvent): void {
    this.selectedPresetId = event.widgetPreset.id;
    this.selectedChartTypeId = event.vizType.id;
    this.selectedEvent = event;
  }

  confirm(): void {
    if (!this.selectedEvent) return;

    this.close({
      reason: DialogCloseReason.Confirm,
      data: {
        chartId: this.selectedEvent.widgetPreset.id,
        chartTypeId: this.selectedEvent.vizType.id,
      },
    });
  }

  cancel(): void {
    this.close({ reason: DialogCloseReason.Cancel });
  }
}
